import React, { type ReactElement, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import AppliedFilters from 'atom-components/applied-filters-lite';
import SingleComboBox, {
  type ComboBoxItem
} from 'atom-components/form-fields-for-hook-form/single-combo-box';
import Label from 'atom-components/label';
import type { YesNoOrBothValue } from 'schemas/yes-no-or-both.schema';
import { filterFormDefaultValues } from './filter-form.schema';

const fieldLabel = 'Comment';
const fieldName = 'comment';

const selectableCommentStates: ComboBoxItem[] = [
  { id: 'Y', name: 'Yes' },
  { id: 'N', name: 'No' }
].map((commentState) => ({ id: commentState.id, name: commentState.name }));

type Props = {
  triggerFormSubmission: () => void;
};

export default function CommentFilter({ triggerFormSubmission }: Props): ReactElement {
  const { control, setValue, watch, resetField } = useFormContext();
  const markingStarted: YesNoOrBothValue = watch('markingStarted');

  const isMarkingStartedNoApplied = markingStarted === 'N';
  useEffect(() => {
    if (isMarkingStartedNoApplied) {
      resetField(fieldName, { defaultValue: filterFormDefaultValues.comment });
    }
  }, [resetField, isMarkingStartedNoApplied]);

  return (
    <div>
      <Label htmlFor={fieldName}>Comment</Label>
      <SingleComboBox
        id={fieldName}
        name={fieldName}
        control={control}
        allItems={selectableCommentStates}
        clearable
        disabled={isMarkingStartedNoApplied}
        onChange={(item): void => {
          const commentState = item?.id || '';
          setValue(fieldName, commentState, {
            shouldValidate: true,
            shouldTouch: true
          });
          triggerFormSubmission();
        }}
      />
    </div>
  );
}

export function CommentAppliedFilter({ triggerFormSubmission }): ReactElement | null {
  const { watch, resetField } = useFormContext();
  const comment = watch(fieldName);

  const commentState = selectableCommentStates.find((cs) => cs.id === comment);
  if (!commentState) return null;

  return (
    <AppliedFilters.Filter name={fieldLabel}>
      <AppliedFilters.FilterItem
        onClick={(): void => {
          resetField(fieldName, { defaultValue: filterFormDefaultValues.comment });
          triggerFormSubmission();
        }}
      >
        {commentState.name}
      </AppliedFilters.FilterItem>
    </AppliedFilters.Filter>
  );
}
